import React, { useEffect, useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import DetailMetaHeader from 'components/condition-list/DetailMetaHeader';
import KatakanaTextField from 'components/form/KatakanaTextField';
import ReadOnlyField from 'components/form/ReadOnlyField';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { MasterTypeResult, getContractType, getGender } from 'stores/MasterTypeStoreAdapter';
import { MemberDetailResult, updateMember } from 'stores/MemberStoreAdapter';
import { UserResult, getUser } from 'stores/UserStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type MemberUpdateFormHandle = {
  submit: () => void;
  isEditing: () => boolean;
};

type MemberUpdateFormProps = {
  member: MemberDetailResult;
  isEditing: boolean;
  onMemberUpdated: () => void;
  onClose: () => void;
  onEditingChange: (editing: boolean) => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const MemberUpdateForm = React.forwardRef<MemberUpdateFormHandle, MemberUpdateFormProps>(
  (
    {
      member,
      isEditing,
      onMemberUpdated,
      onClose,
      onEditingChange,
      setProgress,
      hideHeader = false,
    },
    ref,
  ) => {
    const [memberName, setMemberName] = useState(member.memberName);
    const [memberNameKana, setMemberNameKana] = useState(member.memberNameKana);
    const [genderId, setGenderId] = useState(member.genderId);
    const [userId, setUserId] = useState(member.userId || '');
    const [department, setDepartment] = useState(member.department || '');
    const [contractTypeId, setContractTypeId] = useState(member.contractTypeId || '');
    const [remarks, setRemarks] = useState(member.remarks || '');

    const [genders, setGenders] = useState<MasterTypeResult[]>([]);
    const [contractTypes, setContractTypes] = useState<MasterTypeResult[]>([]);
    const [users, setUsers] = useState<UserResult[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [copyTooltip, setCopyTooltip] = useState('Copy to clipboard');

    const isEmployee = member.employmentTypeId === 'EMPLOYEE';
    const isDeleted = member.isDeleted;

    useEffect(() => {
      Promise.all([getGender(), getContractType(), getUser({})])
        .then(([genTypes, conTypes, userList]) => {
          setGenders(genTypes);
          setContractTypes(conTypes.filter((t) => t.typeCode !== 'EMPLOYEE'));
          setUsers(userList);
        })
        .catch(() => errorDefault('マスタデータの取得に失敗しました'));
    }, []);

    useEffect(() => {
      setMemberName(member.memberName);
      setMemberNameKana(member.memberNameKana);
      setGenderId(member.genderId);
      setUserId(member.userId || '');
      setDepartment(member.department || '');
      setContractTypeId(member.contractTypeId || '');
      setRemarks(member.remarks || '');
      setErrors({});
      onEditingChange(false);
    }, [member]);

    const handleCopyId = () => {
      navigator.clipboard.writeText(member.memberId).then(() => {
        setCopyTooltip('Copied!');
        setTimeout(() => setCopyTooltip('Copy to clipboard'), 2000);
      });
    };

    const handleBackToReadOnly = () => {
      setMemberName(member.memberName);
      setMemberNameKana(member.memberNameKana);
      setGenderId(member.genderId);
      setUserId(member.userId || '');
      setDepartment(member.department || '');
      setContractTypeId(member.contractTypeId || '');
      setRemarks(member.remarks || '');
      setErrors({});
      onEditingChange(false);
    };

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!memberName) newErrors.memberName = MSG_REQUIRED('要員名');
      else if (memberName.length > 300) newErrors.memberName = MSG_MAX_LENGTH('要員名', 300);

      if (!memberNameKana) newErrors.memberNameKana = MSG_REQUIRED('要員名（カナ）');
      else if (memberNameKana.length > 300)
        newErrors.memberNameKana = MSG_MAX_LENGTH('要員名（カナ）', 300);

      if (!genderId) newErrors.genderId = MSG_REQUIRED('性別');

      if (!isEmployee && !contractTypeId) {
        newErrors.contractTypeId = MSG_REQUIRED('契約形態');
      }

      if (remarks && remarks.length > 10000) newErrors.remarks = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
      if (!validate()) return;

      setProgress(true);
      updateMember({
        memberId: member.memberId,
        memberName,
        memberNameKana,
        genderId,
        ...(userId && { userId }),
        ...(department && { department }),
        ...(contractTypeId && { contractTypeId }),
        ...(remarks && { remarks }),
      })
        .then(() => {
          successDefault('要員情報を更新しました');
          onMemberUpdated();
          onEditingChange(false);
        })
        .catch((e: Error) => {
          errorDefault('要員情報の更新に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleSave, isEditing: () => isEditing }));

    const getGenderLabel = (id: string): string => {
      const found = genders.find((g) => g.typeCode === id);
      return found ? found.typeLabel : id;
    };

    const getContractTypeLabel = (id: string): string => {
      const found = contractTypes.find((t) => t.typeCode === id);
      return found ? found.typeLabel : id || '-';
    };

    /** 共通ヘッダー */
    const renderHeader = (title: string, subtitle: string, onBack: () => void) => (
      <Box sx={STICKY_HEADER_SX}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
            <Chip
              label={isEmployee ? '社員' : '社外'}
              size="small"
              color={isEmployee ? 'primary' : 'secondary'}
              sx={{ fontWeight: 700 }}
            />
            {isDeleted && (
              <Chip
                label="削除済"
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {member.memberId}
            </Typography>
            <Tooltip title={copyTooltip}>
              <IconButton size="small" onClick={handleCopyId}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
            {subtitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            作成者: {member.createdByName} 作成日: {formatDate(member.createdAt)}
          </Typography>
        </Stack>
      </Box>
    );

    // --- 参照モード ---
    if (!isEditing) {
      return (
        <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {!hideHeader && renderHeader('要員詳細', '要員情報を確認します', onClose)}

          <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
            {hideHeader && (
              <DetailMetaHeader
                id={member.memberId}
                metaItems={[
                  {
                    label: '作成者',
                    value: `${member.createdByName} (${formatDate(member.createdAt)})`,
                  },
                ]}
              />
            )}
            {/* 基本情報 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                基本情報
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="要員名" value={member.memberName} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="要員名（カナ）" value={member.memberNameKana} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="雇用形態" value={isEmployee ? '社員' : '社外'} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="性別" value={getGenderLabel(member.genderId)} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 所属情報 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                所属
              </Typography>
              <Grid container spacing={2}>
                {isEmployee ? (
                  <>
                    <Grid size={6}>
                      <ReadOnlyField label="ユーザー" value={member.mailAddress || '-'} />
                    </Grid>
                    <Grid size={6}>
                      <ReadOnlyField label="所属" value={member.department || '-'} />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid size={6}>
                      <ReadOnlyField
                        label="個人事業主"
                        value={member.isIndividual ? 'はい' : 'いいえ'}
                      />
                    </Grid>
                    <Grid size={6}>
                      <ReadOnlyField
                        label="パートナー"
                        value={member.partner?.partnerName || '-'}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            {/* 契約形態 (社外のみ) */}
            {!isEmployee && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    契約
                  </Typography>
                  <ReadOnlyField
                    label="契約形態"
                    value={getContractTypeLabel(member.contractTypeId)}
                  />
                </Box>
              </>
            )}

            <Divider />

            {/* 備考 */}
            <ReadOnlyField label="備考" value={member.remarks || ''} />
          </Stack>
        </Stack>
      );
    }

    // --- 編集モード ---
    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader &&
          renderHeader('要員詳細（編集中）', '要員情報を編集します', handleBackToReadOnly)}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          {hideHeader && (
            <DetailMetaHeader
              id={member.memberId}
              metaItems={[
                {
                  label: '作成者',
                  value: `${member.createdByName} (${formatDate(member.createdAt)})`,
                },
              ]}
            />
          )}
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="要員名"
                  value={memberName}
                  onChange={(e) => setMemberName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  disabled={isDeleted}
                  error={!!errors.memberName}
                  helperText={errors.memberName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <KatakanaTextField
                  label="要員名（カナ）"
                  value={memberNameKana}
                  onChange={(v) => setMemberNameKana(v)}
                  fullWidth
                  size="small"
                  required
                  disabled={isDeleted}
                  error={!!errors.memberNameKana}
                  helperText={errors.memberNameKana}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                />
              </Grid>
              <Grid size={6}>
                <ReadOnlyField label="雇用形態" value={isEmployee ? '社員' : '社外'} />
              </Grid>
              <Grid size={12}>
                <FormControl error={!!errors.genderId} required disabled={isDeleted}>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>性別</FormLabel>
                  <RadioGroup row value={genderId} onChange={(e) => setGenderId(e.target.value)}>
                    {genders.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                        disabled={isDeleted}
                      />
                    ))}
                  </RadioGroup>
                  {errors.genderId && <FormHelperText>{errors.genderId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 所属情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              所属
            </Typography>
            <Grid container spacing={2}>
              {isEmployee ? (
                <>
                  <Grid size={6}>
                    <UserSelect
                      mode="single"
                      users={users}
                      selectedUserId={userId || null}
                      onChange={(id) => {
                        setUserId(id || '');
                        if (id) {
                          const user = users.find((u) => u.userId === id);
                          if (user) {
                            setMemberName(user.userName);
                          }
                        }
                      }}
                      label="ユーザー"
                      disabled={isDeleted || isEmployee}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label="所属"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      fullWidth
                      size="small"
                      disabled={isDeleted}
                      slotProps={{ htmlInput: { maxLength: 300 } }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid size={6}>
                    <ReadOnlyField
                      label="個人事業主"
                      value={member.isIndividual ? 'はい' : 'いいえ'}
                    />
                  </Grid>
                  <Grid size={6}>
                    <ReadOnlyField label="パートナー" value={member.partner?.partnerName || '-'} />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* 契約形態 (社外のみ) */}
          {!isEmployee && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  契約
                </Typography>
                <FormControl error={!!errors.contractTypeId} required disabled={isDeleted}>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>契約形態</FormLabel>
                  <RadioGroup
                    row
                    value={contractTypeId}
                    onChange={(e) => setContractTypeId(e.target.value)}
                  >
                    {contractTypes.map((t) => (
                      <FormControlLabel
                        key={t.typeCode}
                        value={t.typeCode}
                        control={<Radio size="small" />}
                        label={t.typeLabel}
                        disabled={isDeleted}
                      />
                    ))}
                  </RadioGroup>
                  {errors.contractTypeId && (
                    <FormHelperText>{errors.contractTypeId}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </>
          )}

          <Divider />

          {/* 備考 */}
          <TextArea
            label="備考"
            value={remarks}
            onChange={setRemarks}
            maxLength={10000}
            rows={6}
            fullWidth
            size="small"
            disabled={isDeleted}
            errorMessage={errors.remarks}
          />
        </Stack>
      </Stack>
    );
  },
);

MemberUpdateForm.displayName = 'MemberUpdateForm';

export default MemberUpdateForm;
